import styles from './edit-profile.module.scss';
import { Divider } from '@mui/material';
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../contexts/userContext';
import { WMenu, WMenuItem, WTypography } from '../../wrappers/wrapper';
import DropzoneModal from '../../organisms/dropzone-modal/dropzone-modal';
import LoadingModal from '../../organisms/loading-modal/loading-modal';
import dbService from '../../../services/database.service';
import { TUser } from '../../../models/common';
import helperService from '../../../services/helper.service';
import spicaStorageService from '../../../services/spica.storage.service';
import Button from 'components/atoms/button/button';
import EditProfileForm from 'components/organisms/edit-profile-form/edit-profile-form';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import { DEFAULT_HTTP_ERR_MESSAGE } from 'utils/constants';

interface EditProfileProps {
  onClose: () => void;
}

const EditProfile: FC<EditProfileProps> = ({ onClose }) => {
  const { getUser, user, setUser } = useContext(UserContext);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const openPopover = Boolean(anchorEl);

  const [avatar, setAvatar] = useState(user.avatar);
  const [loading, setLoading] = useState<boolean>(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [acceptedFiles, setAcceptedFiles] = useState<File>();
  const [tempValues, setTempValues] = useState({
    username: user.username,
    job: user.job,
    biography: user.biography,
    website: user.website,
    company: user.website,
    avatar: user.avatar,
  });
  const [switchValues, setSwitchValues] = useState({
    makeEmailVisible: false,
    preventMessaging: false,
    showProfileInDirectory: true,
  });
  const [imageUrl, setImageUrl] = useState('');

  const [open, setOpen] = useState<boolean>(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const onDrop = useCallback((input: any) => {
    if (!input[0]) {
      return;
    }

    const reader = new FileReader();
    const formData = new FormData();
    formData.append('image', input[0]);

    reader.onload = (event) => {
      if (event.target) {
        setImageUrl(event.target.result as string);
      }
    };
    reader.readAsDataURL(input[0]);

    setAcceptedFiles(input[0]);
    handleClose();
    setUnsavedChanges(true);
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setTempValues((prevValues) => ({
      ...prevValues,
      [field]: value,
    }));
    setUnsavedChanges(true);
  };

  const handleSwitchToggle = (key: keyof typeof switchValues) => {
    setSwitchValues((prevValues) => ({
      ...prevValues,
      [key]: !prevValues[key],
    }));
    setUnsavedChanges(true);
  };

  const updateUser = (avatar?: string) => {
    const updatedUser: TUser = { ...tempValues };
    if (avatar) {
      updatedUser.avatar = avatar;
      setAvatar(avatar);
    }

    dbService
      .updateUser(user._id!, { ...updatedUser })
      .then((_) => setUser({ ...user, ...updatedUser }))
      .catch((_) => helperService.showToast('error', DEFAULT_HTTP_ERR_MESSAGE));
    setLoading(false);
  };

  const insertIntoStorage = async () => {
    if (!acceptedFiles) {
      return;
    }

    const fileExtention = helperService.getFileExtension(acceptedFiles.name);
    const newFileName = `profilePicture/${user._id}_profile_img${fileExtention}`;
    const fileToUpload = new File([acceptedFiles], newFileName);
    return spicaStorageService.upload(fileToUpload);
  };

  const handleSaveChanges = async () => {
    setLoading(true);
    try {
      const res = await insertIntoStorage();
      updateUser(res?.url!);
      getUser();
      setUnsavedChanges(false);
      setAcceptedFiles(undefined);
    } catch (error) {
      console.error('ERROR: ', error);
    }
  };

  const handleCancelChanges = () => {
    setTempValues({
      username: '',
      job: '',
      biography: '',
      website: '',
      company: '',
      avatar: '',
    });
    setSwitchValues({
      makeEmailVisible: false,
      preventMessaging: false,
      showProfileInDirectory: true,
    });
    setUnsavedChanges(false);
    setAcceptedFiles(undefined);
    onClose();
  };

  const handleClickAvatarSettings = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAvatarSetting = () => {
    setAnchorEl(null);
  };

  const handleRemoveAvatar = () => {
    setTempValues((prevValues) => ({ ...prevValues, avatar: '' }));
    setAvatar('');
    setImageUrl('');
    setAcceptedFiles(undefined);
    setUnsavedChanges(true);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <span className={styles.title}>Profile</span>

        <div className={styles.topContent}>
          <div className={styles.topContentLeft}>
            <span className={styles.sectionTitle}>Profile photo</span>
            <span className={styles.recommended}>Recommended 300x300</span>
          </div>
          <div>
            <div>
              {!avatar && !acceptedFiles ? (
                <Button radius="10rem" type="primaryOutline" onClick={handleOpen}>
                  Upload Photo
                </Button>
              ) : (
                <div className={styles.avatar}>
                  <img src={avatar || imageUrl} alt="Avatar" />
                  <Button
                    type="base"
                    className={styles.changeAvatar}
                    onClick={handleClickAvatarSettings}
                  >
                    <AddAPhotoIcon fontSize="small" />
                  </Button>
                  <WMenu
                    anchorEl={anchorEl}
                    open={openPopover}
                    onClose={handleCloseAvatarSetting}
                    onClick={handleCloseAvatarSetting}
                    slotProps={{
                      paper: {
                        elevation: 0,
                        sx: {
                          border: 'var(--border)',
                          padding: '0.5rem',
                          width: '10rem',
                          borderRadius: 'var(--border-radius)',
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    className={styles.menu}
                  >
                    <WMenuItem onClick={handleOpen}>Change avatar</WMenuItem>
                    <WMenuItem onClick={handleRemoveAvatar}>Remove</WMenuItem>
                  </WMenu>
                </div>
              )}
            </div>

            <DropzoneModal open={open} handleClose={handleClose} onDrop={onDrop} />
          </div>
        </div>
        <EditProfileForm
          values={tempValues}
          switchValues={switchValues}
          onInputChange={handleInputChange}
          onSwitchToggle={handleSwitchToggle}
        />
      </div>
      <Divider style={{border:"var(--border-light)"}} />
      <div className={styles.bottom}>
        <div className={styles.bottomContent}>
          <WTypography className={styles.warning}>
            {unsavedChanges && 'You have unsaved changes'}
          </WTypography>
          <div className={styles.actions}>
            <Button type={'primaryOutline'} onClick={handleCancelChanges}>
              Cancel
            </Button>
            <Button onClick={handleSaveChanges} disabled={!unsavedChanges}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>
      {loading && <LoadingModal loading={loading} />}
    </div>
  );
};
export default EditProfile;
